import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Truck, CheckCircle, ArrowLeft, SkipForward } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../store';
import { logoutMdt } from '../lib/logoutMdt';
import { cn, API_BASE_URL } from '../lib/utils';
import type { Resource } from '../types';
import MdtLogoutConfirmModal from '../components/mobile/MdtLogoutConfirmModal';

interface MdtResourceSelectProps {
  selectedEvent: any;
  onSelect: (resource: Resource) => void;
}

export default function MdtResourceSelect({ selectedEvent, onSelect }: MdtResourceSelectProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((state) => state.auth);
  const [resources, setResources] = useState<Resource[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (!token) return;
    const url = selectedEvent
      ? `${API_BASE_URL}/resources?eventId=${selectedEvent.id}`
      : `${API_BASE_URL}/resources`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setResources(d.resources || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token, selectedEvent]);

  const attemptClaim = async (res: Resource, force = false): Promise<void> => {
    const claimRes = await fetch(`${API_BASE_URL}/resources/${encodeURIComponent(res.id)}/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ force, eventId: selectedEvent?.id }),
    });

    if (claimRes.ok) {
      const data = await claimRes.json();
      const resource = {
        ...data.resource,
        eventId: selectedEvent?.id || data.resource?.eventId,
      };
      localStorage.setItem('c2_mdt_resource', JSON.stringify(resource));
      onSelect(resource);
      return;
    }

    const error = await claimRes.json();
    if (claimRes.status === 409 && error.canForce) {
      const ageMin = error.ageSeconds ? Math.round(error.ageSeconds / 60) : null;
      const ageText = ageMin !== null
        ? (ageMin < 1 ? `${error.ageSeconds}s ago` : `${ageMin}m ago`)
        : 'unknown';
      const ok = window.confirm(
        `${error.error}\n\nLast active: ${ageText}\n\nTake over this unit anyway?`
      );
      if (ok) {
        await attemptClaim(res, true);
      }
      return;
    }

    alert(error.error || 'Failed to claim resource');
  };

  const handleConfirm = async () => {
    const res = resources.find((r) => r.id === selected);
    if (!res) return;
    try {
      await attemptClaim(res);
    } catch {
      alert('Failed to claim resource');
    }
  };

  const handleSkip = () => {
    const tempResource: Resource = {
      id: 'temp-' + Date.now(),
      name: 'Unassigned',
      type: 'personnel',
      status: 'available',
      department: user?.role || 'MDT',
      eventId: selectedEvent?.id,
      location: { latitude: 0, longitude: 0 },
      capabilities: [],
      lastUpdate: new Date().toISOString(),
    };
    localStorage.setItem('c2_mdt_resource', JSON.stringify(tempResource));
    onSelect(tempResource);
  };

  const handleLogoutConfirm = async () => {
    setLoggingOut(true);
    try {
      await logoutMdt(dispatch, navigate, token);
    } finally {
      setLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  const statusColor: Record<string, string> = {
    available: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    deployed: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    en_route: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
    maintenance: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
    offline: 'text-red-400 bg-red-400/10 border-red-400/20',
    reserved: 'text-gray-400 bg-gray-400/10 border-gray-400/20',
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex flex-col items-center justify-center p-6">
      {/* Background gradient */}
      <div className="fixed inset-0 bg-gradient-to-b from-blue-900/20 via-transparent to-transparent pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
            style={{ background: 'rgba(102,148,255,0.2)', border: '1px solid rgba(102,148,255,0.15)' }}
          >
            <Shield className="w-7 h-7 text-blue-400" />
          </div>
          <h1 className="text-xl font-bold text-white">Select Your Unit</h1>
          <p className="text-sm text-white/40 mt-1">
            Welcome, {user?.firstName}. Choose the resource you are operating.
          </p>
        </div>

        {/* Resource list */}
        <div className="space-y-2 mb-6 max-h-[50vh] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-white/30 text-sm">Loading resources...</div>
          ) : resources.length === 0 ? (
            <div className="text-center py-8 text-white/30 text-sm">No resources available</div>
          ) : (
            resources.map((res) => {
              const r = res as Resource & { inUseBy?: string | null; lastHeartbeat?: string | null };
              const isInUse = !!r.inUseBy && r.inUseBy !== user?.id;
              const isMyResource = r.inUseBy === user?.id;
              const heartbeatAge = r.lastHeartbeat ? Date.now() - new Date(r.lastHeartbeat).getTime() : Infinity;
              const isStaleClaim = isInUse && heartbeatAge > 60_000; // >60s = stale
              const canSelect = !isInUse || isStaleClaim;
              return (
                <button
                  key={res.id}
                  onClick={() => canSelect && setSelected(res.id)}
                  disabled={!canSelect}
                  className={cn(
                    'w-full flex items-center gap-3 p-3.5 rounded-xl text-left transition-all active:scale-[0.98]',
                    selected === res.id
                      ? 'bg-blue-500/15 border-[1.5px] border-blue-400/40'
                      : !canSelect
                      ? 'border-[1.5px] border-red-400/20 bg-red-500/5 opacity-50 cursor-not-allowed'
                      : isStaleClaim
                      ? 'border-[1.5px] border-amber-400/20 bg-amber-500/5 hover:bg-amber-500/10'
                      : 'border-[1.5px] border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
                  )}
                >
                  <div className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
                    selected === res.id ? 'bg-blue-500/20' : !canSelect ? 'bg-red-500/10' : isStaleClaim ? 'bg-amber-500/10' : 'bg-white/[0.04]'
                  )}>
                    <Truck className={cn('w-5 h-5', selected === res.id ? 'text-blue-400' : !canSelect ? 'text-red-400' : isStaleClaim ? 'text-amber-400' : 'text-white/30')} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn('text-sm font-bold', selected === res.id ? 'text-white' : !canSelect ? 'text-red-400' : isStaleClaim ? 'text-amber-300' : 'text-white/80')}>
                        {res.name}
                      </span>
                      <span className={cn('text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded border', statusColor[res.status] || 'text-gray-400')}>
                        {res.status.replace('_', ' ')}
                      </span>
                      {isMyResource && (
                        <span className="text-[8px] font-semibold uppercase px-1.5 py-0.5 rounded border bg-green-500/10 text-green-400 border-green-500/20">
                          Yours
                        </span>
                      )}
                      {isInUse && !isStaleClaim && (
                        <span className="text-[8px] font-semibold uppercase px-1.5 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/20">
                          In Use
                        </span>
                      )}
                      {isStaleClaim && (
                        <span className="text-[8px] font-semibold uppercase px-1.5 py-0.5 rounded border bg-amber-500/10 text-amber-400 border-amber-500/20">
                          Stale — Tap to Take Over
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/30 mt-0.5">
                      {res.department} &middot; {res.type} &middot; {res.id}
                    </div>
                  </div>
                  {selected === res.id && (
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Buttons */}
        <div className="space-y-2">
          {/* Confirm button */}
          <button
            onClick={handleConfirm}
            disabled={!selected}
            className={cn(
              'w-full py-3 rounded-xl text-sm font-bold transition-all',
              selected
                ? 'bg-blue-500 text-white hover:bg-blue-400 active:scale-[0.98]'
                : 'bg-white/[0.04] text-white/20 border border-white/[0.06] cursor-not-allowed'
            )}
          >
            {selected ? 'Confirm & Enter MDT' : 'Select a resource to continue'}
          </button>

          {/* Skip button */}
          <button
            onClick={handleSkip}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all bg-white/[0.04] text-white/60 border border-white/[0.06] hover:bg-white/[0.08] active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <SkipForward className="w-4 h-4" />
            Continue Without Resource
          </button>

          {/* Logout button */}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full py-3 rounded-xl text-sm font-bold transition-all bg-red-500/10 text-red-400 border border-red-400/20 hover:bg-red-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
        </div>

        <MdtLogoutConfirmModal
          open={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={handleLogoutConfirm}
          loggingOut={loggingOut}
        />

        <p className="text-center text-[10px] text-white/20 mt-4">
          Your location will be tracked while signed into this unit.
        </p>
      </div>
    </div>
  );
}
